import { useState } from 'react';
import { CheckIcon, DuplicateIcon } from '@heroicons/react/outline';
import toast from 'react-hot-toast';

import {
  PatientContact,
  ProgramNoteShareStatus,
  useProgramNoteSharesForContactQuery,
  useRevokeProgramNoteShareMutation,
} from '../../../../generated/graphql';
import { getContactFullName } from '../../../pages/PatientProfile/Account/Contacts/constants';
import ModalDialog from '../../ModalDialog';
import Button from '../../Button';
import { customToast } from '../../ToastAlert/customToast';

type ViewSentNotesModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  contact: PatientContact | null;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function StatusBadge({ status }: { status: ProgramNoteShareStatus }) {
  const statusConfig = {
    [ProgramNoteShareStatus.Active]: {
      label: 'Active',
      className: 'bg-green-25',
    },
    [ProgramNoteShareStatus.Expired]: {
      label: 'Expired',
      className: 'bg-neutral-50',
    },
    [ProgramNoteShareStatus.Revoked]: {
      label: 'Revoked',
      className: 'bg-orange-25',
    },
  };
  const config =
    statusConfig[status] ?? statusConfig[ProgramNoteShareStatus.Active];
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-caption text-neutral-150 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function ViewSentNotesModal({
  isOpen,
  setClosed,
  contact,
}: ViewSentNotesModalProps) {
  const { data, loading, error, refetch } = useProgramNoteSharesForContactQuery(
    {
      variables: { contactId: contact?.id ?? '' },
      skip: !isOpen || !contact?.id,
      fetchPolicy: 'network-only',
    },
  );

  const [revokeShare, { loading: isRevoking }] =
    useRevokeProgramNoteShareMutation();
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);
  const [revokingShareId, setRevokingShareId] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const shares = data?.programNoteSharesForContact ?? [];

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCopyLink = async (shareId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
      setCopiedShareId(shareId);
      setTimeout(() => setCopiedShareId(null), 3000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleRevoke = async (shareId: string) => {
    setRevokingShareId(shareId);
    try {
      await revokeShare({ variables: { shareId } });
      customToast.success('Share link revoked');
      await refetch();
    } catch {
      customToast.error('Unable to revoke share link. Please try again.');
    } finally {
      setRevokingShareId(null);
    }
  };

  return (
    <ModalDialog
      title={`Notes sent to ${
        contact ? getContactFullName(contact) : 'this contact'
      }`}
      isOpen={isOpen}
      setClosed={setClosed}
      hideFooter
      width="medium"
      overflow="auto"
    >
      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="py-8 text-center text-caption text-neutral-125">
            Loading...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-center text-caption text-neutral-125">
              Unable to load sent notes. Please try again.
            </p>
            <Button
              title={isRetrying ? 'Retrying...' : 'Retry'}
              size="small"
              theme="secondary"
              disabled={isRetrying}
              onClick={handleRetry}
            />
          </div>
        ) : shares.length === 0 ? (
          <p className="py-6 text-center text-caption text-neutral-125">
            No note links have been sent to this contact yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {shares.map((share) => (
              <li
                key={share.id}
                className="flex flex-col gap-2 rounded border border-neutral-50 bg-neutral-50 p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-neutral-150">
                      {share.noteTitle || 'Untitled note'}
                    </p>
                    <p className="mt-1 text-small-caption text-neutral-125">
                      Sent {formatDate(share.sentAt)} · Expires{' '}
                      {formatDate(share.expiresAt)}
                    </p>
                    {share.firstViewedAt && (
                      <p className="mt-0.5 text-small-caption text-neutral-125">
                        First viewed {formatDate(share.firstViewedAt)}
                        {share.downloadCount > 0
                          ? ` · ${share.downloadCount} PDF download${
                              share.downloadCount === 1 ? '' : 's'
                            }`
                          : ''}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={share.status} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {share.status === ProgramNoteShareStatus.Active && (
                    <>
                      <Button
                        title="Copy link"
                        size="small"
                        theme="secondary"
                        IconComponent={
                          copiedShareId === share.id ? CheckIcon : DuplicateIcon
                        }
                        iconPosition="left"
                        iconClassName={
                          copiedShareId === share.id
                            ? 'text-green-150'
                            : undefined
                        }
                        onClick={() => handleCopyLink(share.id, share.url)}
                      />
                      <Button
                        title="Revoke"
                        size="small"
                        theme="secondary"
                        disabled={isRevoking && revokingShareId === share.id}
                        onClick={() => handleRevoke(share.id)}
                      />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalDialog>
  );
}
